package com.crowpay.views.theming

import com.crowpay.views.components.nav.AppDimensions
import com.lightningkite.kiteui.models.ButtonSemantic
import com.lightningkite.kiteui.models.CardSemantic
import com.lightningkite.kiteui.models.Color
import com.lightningkite.kiteui.models.CornerRadii
import com.lightningkite.kiteui.models.DisabledSemantic
import com.lightningkite.kiteui.models.DownSemantic
import com.lightningkite.kiteui.models.EmphasizedSemantic
import com.lightningkite.kiteui.models.HoverSemantic
import com.lightningkite.kiteui.models.Paint
import com.lightningkite.kiteui.models.SelectedSemantic
import com.lightningkite.kiteui.models.Semantic
import com.lightningkite.kiteui.models.Theme
import com.lightningkite.kiteui.models.ThemeAndBack
import com.lightningkite.kiteui.models.UnselectedSemantic
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.rem

private fun ColorSet.matchBrightness(other: Paint, skew: Float = 1f) = toList().let {
    it[((it.size - 1) * other.closestColor().perceivedBrightness.times(skew).coerceAtMost(1f)).toInt()]
}

data object ReviewSelectionSemantic : Semantic {
    override val key: String = "slc4rvw"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        derivations = mapOf(
            SelectedSemantic to {
                it.copy(
                    id = "sel",
                    background = AppColors.purple.matchBrightness(it.background, 1.2f)
                ).withBack
            },
            DisabledSemantic to {
                it.withoutBack
            },
            UnselectedSemantic to {
                it.withoutBack
            }
        )
    ).withoutBack
}

data object ApprovedSemantic : Semantic {
    override val key: String = "aprvd"
    override fun default(theme: Theme): ThemeAndBack {
        return theme.copy(
            id = key,
            derivations = mapOf(
                CardSemantic to {
                    val green = AppColors.secondary.matchBrightness(it.background, 1.1f)
                    it.copy(
                        id = "crd",
                        foreground = if (green.perceivedBrightness > 0.5) Color.black else Color.white,
                        background = green,
                        cornerRadii = CornerRadii.Constant(AppDimensions.cornerRadii),
                    ).withBack
                },
                EmphasizedSemantic to {
                    it.copy(
                        id = "emph",
                        foreground = AppColors.secondary.main,
                    ).withoutBack
                }
            )
        ).withoutBack
    }
}

data object DeniedSemantic : Semantic {
    override val key: String = "dnid"
    override fun default(theme: Theme): ThemeAndBack {
        return theme.copy(
            id = key,
            derivations = mapOf(
                CardSemantic to {
                    val red = AppColors.red.matchBrightness(it.background)
                    it.copy(
                        id = "crd",
                        foreground = if (red.perceivedBrightness > 0.5) Color.black else Color.white,
                        background = red,
                        cornerRadii = CornerRadii.Constant(AppDimensions.cornerRadii),
                    ).withBack
                },
                EmphasizedSemantic to {
                    it.copy(
                        id = "emph",
                        foreground = AppColors.red.main,
                    ).withoutBack
                }
            )
        ).withoutBack
    }
}

data object ApprovalToggles : Semantic {
    override val key: String = "apvltgl"
    override fun default(theme: Theme): ThemeAndBack {
        val oldBack = theme.background
        return theme.copy(
            id = key,
            spacing = 0.5.rem,
            background = Color.transparent,
            derivations = mapOf(
                ButtonSemantic to {
                    it.copy(
                        id = "btn",
                        spacing = 0.95.rem
                    )
                    .withBack
                },
                HoverSemantic to {
                    val back = it.background.closestColor()
                    it
                        .copy(id = "hvr", background = if (back == Color.white) back else AppColors.grey.light2.lighten(0.6f))
                        .withBack
                },
                DownSemantic to {
                    val back = it.background.closestColor()
                    it
                        .copy(id = "dwn", background = if (back == Color.white) back else AppColors.grey.light2.lighten(0.8f))
                        .withBack
                },
                ApprovedSemantic to { approved ->
                    approved.copy(
                        id = "aprvd",
                        derivations = mapOf(
                            SelectedSemantic to {
                                it.copy(
                                    id = "sel",
                                    foreground = AppColors.secondary.main,
//                                    iconOverride = AppColors.secondary.main,
                                    background = oldBack,
                                    font = it.font.copy(bold = true),
                                    outline = AppColors.grey.light2,
                                    outlineWidth = 4.dp,
                                ).withBack
                            },
                            UnselectedSemantic to {
                                it.copy(
                                    id = "unsel",
                                    foreground = Color.gray(0.1f),
                                    iconOverride = null,
                                    background = AppColors.grey.light2,
                                    outline = AppColors.grey.light2,
                                    outlineWidth = 4.dp,
                                ).withBack
                            },
                        )
                    ).withoutBack
                },
                DeniedSemantic to { denied ->
                    denied.copy(
                        id = "dnid",
                        derivations = mapOf(
                            SelectedSemantic to {
                                it.copy(
                                    id = "sel",
                                    foreground = AppColors.red.main,
//                                    iconOverride = AppColors.red.main,
                                    background = oldBack,
                                    font = it.font.copy(bold = true),
                                    outline = AppColors.grey.light2,
                                    outlineWidth = 4.dp,
                                ).withBack
                            },
                            UnselectedSemantic to {
                                it.copy(
                                    id = "unsel",
                                    foreground = Color.gray(0.1f),
                                    iconOverride = null,
                                    background = AppColors.grey.light2,
                                    outline = AppColors.grey.light2,
                                    outlineWidth = 4.dp,
                                ).withBack
                            },
                        )
                    ).withoutBack
                },
            ),
        ).withoutBack
    }
}